import React from 'react';

import i18next from '../../../base/i18n/i18next';
import { Icon, IconArrowDown } from '../../../base/icons';
import { getBrandingColorPalette } from '../../../base/styles';
import { MESSAGE_TYPE_REMOTE } from '../../constants';
import AbstractMessageContainer, { type Props }
    from '../AbstractMessageContainer';

import ChatMessageGroup from './ChatMessageGroup';

declare var interfaceConfig: Object;

/**
 * Number of message groups rendered when the container mounts. Visitor chats
 * in large webinars can hold thousands of messages, so only a window at the
 * tail of the list is rendered and older history is added on demand.
 */
const INITIAL_GROUPS_TO_RENDER = 50;

/**
 * Number of additional (older) message groups rendered per load request.
 */
const GROUPS_PER_LOAD = 50;

/**
 * Distance in px from the top of the scroll container at which older message
 * groups are loaded automatically.
 */
const LOAD_MORE_SCROLL_THRESHOLD = 120;

/**
 * Displays all received chat messages, grouped by sender.
 *
 * @augments AbstractMessageContainer
 */
export default class MessageContainer extends AbstractMessageContainer<Props> {
    /**
     * Whether or not chat has been scrolled to the bottom of the screen. Used
     * to determine if chat should be scrolled automatically to the bottom when
     * the {@code ChatInput} resizes.
     */
    _isScrolledToBottom: boolean;

    /**
     * Reference to the HTML element at the end of the list of displayed chat
     * messages. Used for scrolling to the end of the chat messages.
     */
    _messagesListEndRef: Object;

    /**
     * A React ref to the HTML element containing all {@code ChatMessageGroup}
     * instances.
     */
    _messageListRef: Object;

    /**
     * Initializes a new {@code MessageContainer} instance.
     *
     * @param {Props} props - The React {@code Component} props to initialize
     * the new {@code MessageContainer} instance with.
     */
    constructor(props: Props) {
        super(props);

        this._isScrolledToBottom = true;
        this._lastClientHeight = -1;
        this._lastScrollHeight = -1;

        this._messageListRef = React.createRef();
        this._messagesListEndRef = React.createRef();

        this._onChatScroll = this._onChatScroll.bind(this);
        this._onScrollToBottomBtnClick = this._onScrollToBottomBtnClick.bind(this);
        this._onLoadOlderMessages = this._onLoadOlderMessages.bind(this);

        // State to track the last shown date across all message groups
        this.state = {
            lastShownDate: null,
            isScrolledToBottom: true,
            hasNewMessages: false,

            // Number of message groups (counted from the newest) currently
            // rendered. Grows as the user scrolls back through history.
            visibleGroupsCount: INITIAL_GROUPS_TO_RENDER
        };

        // Total group count committed to the DOM. Used to detect how many new
        // groups were appended by an incoming message.
        this._committedTotalGroups = 0;

        // Scroll metrics captured right before older history is prepended, so
        // the viewport can be re-anchored after the DOM grows above it.
        this._pendingScrollAdjustment = false;
        this._scrollHeightBeforeLoad = 0;
        this._scrollTopBeforeLoad = 0;

        this._handleDateShown = this._handleDateShown.bind(this);
    }

    /**
     * Implements {@code Component#componentDidMount}.
     */
    componentDidMount() {
        if (this._messageListRef.current) {
            this.resizeObserver = new ResizeObserver(() => {
                const element = this._messageListRef.current;

                if (element) {
                    const isScrollable = element.scrollHeight > element.clientHeight;

                    if (!isScrollable && !this.state.isScrolledToBottom) {
                        this._isScrolledToBottom = true;
                        this.setState({ isScrolledToBottom: true,
                            hasNewMessages: false });
                    } else if (this._isScrolledToBottom) {
                        this.scrollToBottom(false);
                    }
                }
            });
            this.resizeObserver.observe(this._messageListRef.current);
        }

        this._committedTotalGroups = this._getMessagesGroupedBySender().length;

        // Use a short timeout to ensure the DOM has settled before initial scroll
        setTimeout(() => {
            this.scrollToBottom(false);
        }, 100);
    }

    /**
     * Implements {@code Component#componentDidUpdate}.
     */
    componentDidUpdate(prevProps: Props, prevState: Object) {
        const element = this._messageListRef.current;

        // Older history was just prepended: re-anchor the viewport so the
        // messages the user was looking at stay in place.
        if (this._pendingScrollAdjustment
                && prevState.visibleGroupsCount !== this.state.visibleGroupsCount) {
            this._pendingScrollAdjustment = false;

            if (element) {
                element.scrollTop
                    = (element.scrollHeight - this._scrollHeightBeforeLoad) + this._scrollTopBeforeLoad;
            }
        }

        if (this.props.messages !== prevProps.messages) {
            const totalGroups = this._getMessagesGroupedBySender().length;
            const appendedGroups = totalGroups - this._committedTotalGroups;

            this._committedTotalGroups = totalGroups;

            // The render window is anchored to the newest message. If the user
            // is reading history while new groups arrive, widen the window by
            // the same amount so the currently visible groups don't get pushed
            // out at the top.
            if (!this._isScrolledToBottom && appendedGroups > 0) {
                this.setState(({ visibleGroupsCount }) => {
                    return {
                        visibleGroupsCount: visibleGroupsCount + appendedGroups
                    };
                });
            }

            if (element) {
                const isScrollable = element.scrollHeight > element.clientHeight;

                if (!this._isScrolledToBottom && isScrollable) {
                    this.setState({ hasNewMessages: true });
                }
            }
        }
    }

    /**
     * Implements {@code Component#componentWillUnmount}.
     */
    componentWillUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    /**
     * Callback to track the last shown date across all message groups
     * @param {string} date - The date that was just shown
     */
    _handleDateShown(date) {
        this.setState({ lastShownDate: date });
    }

    /**
     * Gets the formatted date for the header
     * @param {number} timestamp - The timestamp to format
     * @returns {string}
     */
    _getFormattedDateHeader(timestamp) {
        if (!timestamp) {
            return '';
        }

        const date = new Date(timestamp);
        const today = new Date();
        const yesterday = new Date(today);

        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return i18next.t('dateUtils.today');
        } else if (date.toDateString() === yesterday.toDateString()) {
            return i18next.t('dateUtils.yesterday');
        }

        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }

    /**
     * Determines if a date header should be shown
     * @param {Array} currentGroup - Current message group
     * @param {Array} previousGroup - Previous message group
     * @returns {Object} - {shouldShow, timestamp}
     */
    _shouldShowDateHeader(currentGroup, previousGroup) {
        if (!currentGroup || !currentGroup.length || !currentGroup[0].timestamp) {
            return { shouldShow: false,
                timestamp: null };
        }

        const currentDate = new Date(currentGroup[0].timestamp).toDateString();

        // For the first group, always show date header
        if (!previousGroup) {
            return { shouldShow: true,
                timestamp: currentGroup[0].timestamp };
        }

        // Check if previous group exists and has messages
        if (!previousGroup.length || !previousGroup[previousGroup.length - 1].timestamp) {
            return { shouldShow: true,
                timestamp: currentGroup[0].timestamp };
        }

        // Compare with the last message of the previous group
        const previousDate = new Date(previousGroup[previousGroup.length - 1].timestamp).toDateString();

        return {
            shouldShow: currentDate !== previousDate,
            timestamp: currentGroup[0].timestamp
        };
    }

    /**
     * Implements {@code Component#render}.
     *
     * @inheritdoc
     */
    render() {
        const groupedMessages = this._getMessagesGroupedBySender();
        const totalGroups = groupedMessages.length;

        // Only render the newest window of groups. Older history stays out of
        // the DOM until the user scrolls up (massive visitor chats would
        // otherwise render thousands of nodes at once).
        const startIndex = Math.max(0, totalGroups - this.state.visibleGroupsCount);
        const visibleGroups = groupedMessages.slice(startIndex);
        const hasHiddenHistory = startIndex > 0;

        const messages = visibleGroups.map((group, index) => {
            const messageType = group[0] && group[0].messageType;

            // The first rendered group always gets a date header so the top of
            // the window has date context even when older groups are hidden.
            const previousGroup = index > 0 ? visibleGroups[index - 1] : null;
            const dateInfo = this._shouldShowDateHeader(group, previousGroup);
            const dateHeadStyle = { backgroundColor: interfaceConfig.applyMeetingStyle ? getBrandingColorPalette(interfaceConfig).primary : '#1f1f1f' };

            return (
                <React.Fragment key = { startIndex + index }>
                    {dateInfo.shouldShow && (
                        <div
                            className = 'date-head'
                            style = { dateHeadStyle }>
                            {this._getFormattedDateHeader(dateInfo.timestamp)}
                        </div>
                    )}
                    <ChatMessageGroup
                        className = { messageType || MESSAGE_TYPE_REMOTE }
                        messages = { group } />
                </React.Fragment>
            );
        });

        const hasNewMessages = this.state.hasNewMessages;
        const buttonStyle = {
            position: 'absolute',
            bottom: '100px', // slightly above the chat input
            right: '20px',
            backgroundColor: '#00000080',
            color: 'white',
            border: 'none',
            borderRadius: hasNewMessages ? '20px' : '50%',
            height: '35px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 10,
            padding: hasNewMessages ? '0 16px' : '0',
            width: hasNewMessages ? 'auto' : '35px',
            gap: '8px',
            fontFamily: '"Manrope", sans-serif',
            fontSize: '13px',
            fontWeight: '600',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
        };

        const st_1 = {
            position: 'relative',
            bottom: '-20px'
        };

        const st_2 = { marginRight: '4px' };

        return (
            <React.Fragment>
                <div
                    id = 'chatconversation'
                    onScroll = { this._onChatScroll }
                    ref = { this._messageListRef }>
                    {hasHiddenHistory && (
                        <button
                            className = 'chat-load-older'
                            onClick = { this._onLoadOlderMessages }
                            type = 'button'>
                            {i18next.t('chat.loadOlderMessages')}
                        </button>
                    )}
                    {messages}
                    <div
                        ref = { this._messagesListEndRef }
                        style = { st_1 } />
                </div>
                {!this.state.isScrolledToBottom && (
                    <button
                        className = 'jump-to-latest-btn'
                        onClick = { this._onScrollToBottomBtnClick }
                        style = { buttonStyle }
                        title = { hasNewMessages ? 'New Message' : i18next.t('chat.jumpToLatest') || 'Jump to latest message' }>
                        {hasNewMessages && <span style = { st_2 }>{i18next.t('chat.newMessages')}</span>}
                        <Icon
                            color = 'white'
                            size = { 15 }
                            src = { IconArrowDown } />
                    </button>
                )}
            </React.Fragment>
        );
    }

    /**
     * Callback invoked when the "jump to latest" button is clicked. Scrolls the
     */
    _onScrollToBottomBtnClick() {
        this.scrollToBottom(true);
    }

    _onLoadOlderMessages: () => void;

    /**
     * Widens the render window with older message groups, capturing the scroll
     * metrics first so {@code componentDidUpdate} can keep the viewport
     * anchored on the messages currently in view.
     *
     * @private
     * @returns {void}
     */
    _onLoadOlderMessages() {
        const totalGroups = this._getMessagesGroupedBySender().length;

        if (this._pendingScrollAdjustment
                || this.state.visibleGroupsCount >= totalGroups) {
            return;
        }

        const element = this._messageListRef.current;

        if (element) {
            this._scrollHeightBeforeLoad = element.scrollHeight;
            this._scrollTopBeforeLoad = element.scrollTop;
        }

        this._pendingScrollAdjustment = true;
        this.setState(({ visibleGroupsCount }) => {
            return {
                visibleGroupsCount: visibleGroupsCount + GROUPS_PER_LOAD
            };
        });
    }

    /**
     * Scrolls to the bottom again if the instance had previously been scrolled
     * to the bottom. This method is used when a resize has occurred below the
     * instance and bottom scroll needs to be maintained.
     *
     * @returns {void}
     */
    maybeUpdateBottomScroll() {
        if (this._isScrolledToBottom) {
            this.scrollToBottom(false);
        }
    }

    /**
     * Automatically scrolls the displayed chat messages down to the latest.
     *
     * @param {boolean} withAnimation - Whether or not to show a scrolling
     * animation.
     * @returns {void}
     */
    scrollToBottom(withAnimation: boolean) {
        this._isScrolledToBottom = true;
        if (this.state.isScrolledToBottom !== true || this.state.hasNewMessages !== false) {
            this.setState({ isScrolledToBottom: true,
                hasNewMessages: false });
        }
        if (this._messagesListEndRef.current) {
            this._messagesListEndRef.current.scrollIntoView({
                behavior: withAnimation ? 'smooth' : 'auto',
                block: 'nearest'
            });
        }
    }

    _getMessagesGroupedBySender: () => Array<Array<Object>>;

    _onChatScroll: () => void;

    /**
     * Callback invoked to listen to the current scroll location.
     *
     * @private
     * @returns {void}
     */
    _onChatScroll() {
        const element = this._messageListRef.current;

        if (!element || element.scrollHeight === 0) {
            return;
        }

        const isScrollable = element.scrollHeight > element.clientHeight;
        const isScrolledToBottom = !isScrollable || Math.abs(element.scrollHeight - element.scrollTop - element.clientHeight) < 190;

        // Detect if this scroll was purely due to a resize
        const isResize = this._lastClientHeight !== element.clientHeight || this._lastScrollHeight !== element.scrollHeight;

        this._lastClientHeight = element.clientHeight;
        this._lastScrollHeight = element.scrollHeight;

        if (isResize) {
            // Ignore scroll events that happen simultaneously with a layout change.
            // The browser often forcefully changes scrollTop during resize (like entering PiP),
            // and we don't want to mistake that for the user scrolling up.
            if (!isScrollable && !this.state.isScrolledToBottom) {
                this._isScrolledToBottom = true;
                this.setState({ isScrolledToBottom: true,
                    hasNewMessages: false });
            }

            return;
        }

        // Nearing the top of the rendered window: pull in older history so
        // scrolling back through a massive chat feels continuous.
        if (element.scrollTop < LOAD_MORE_SCROLL_THRESHOLD) {
            this._onLoadOlderMessages();
        }

        this._isScrolledToBottom = isScrolledToBottom;

        const nextState = {};
        let stateChanged = false;

        if (this.state.isScrolledToBottom !== isScrolledToBottom) {
            nextState.isScrolledToBottom = isScrolledToBottom;
            stateChanged = true;
        }

        if (isScrolledToBottom && this.state.hasNewMessages) {
            nextState.hasNewMessages = false;
            stateChanged = true;
        }

        if (stateChanged) {
            this.setState(nextState);
        }
    }
}
